import $ from "jquery";
import React, { useRef, useState, useEffect, useCallback, useContext } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";

import { useNavigate, useParams } from "react-router-dom";
import MDLink from "components/MDLink";
import {
  Box,
  Grid,
  Button,
  CircularProgress,
  FormHelperText,
  IconButton,
  Tooltip,
  Checkbox,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import MDAutocomplete from "components/MDAutocomplete";
import {
  getFormElements,
  getSingleFormData,
  updateForm,
  getAllTemplateForms,
  getAllTemplateFormsApi,
} from "lib/Api/FormBuilder";
import { FormBuilderContext } from "context/FormBuilderCtx";
import { LoginFormContext } from "context/loginFormCtx";
import {
  VALIDATION_MESSAGES,
  DRAG_AREA_TEXT,
  CUSTOM_FIELD_NAME,
  NOT_TIME_FIELDS,
  CHILD_FORM_DEFAULT_COLUMNS,
  CHILD_FORM_MANDATORY_COLUMNS,
} from "utils/constants"; // Constants
import isEqual from "lodash/isEqual"; // For unsaved changes detection
import ViewFormPreview from "./ViewFormPreview"; // Form preview dialog
import "../../style/scss/FormBuilder/index.css"; // Styles
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

/**
 * XMLHttpRequest Interceptor for Form Builder Language File
 * 
 * PROBLEM:
 * The jQuery Form Builder library makes an unnecessary AJAX request to '/assets/lang/en-US.lang' 
 * on initialization. This request always results in a 404 error since the file doesn't exist,
 * but it still causes network traffic, console errors, and potentially slow page loads
 * (especially in staging/UAT environments where the 404 response can take 10-20+ seconds).
 * 
 * SOLUTION:
 * We intercept all XMLHttpRequest calls (the foundation of all AJAX requests) and:
 * 1. Identify requests for the language file
 * 2. Block those specific requests from actually hitting the network
 * 3. Simulate a 404 response that matches what would happen normally
 * 4. Let the form builder handle the 404 gracefully as it would anyway
 * 
 * BENEFITS:
 * - Eliminates unnecessary network requests
 * - Improves page load performance
 * - Reduces server load
 * - Maintains expected behavior without modifying the library
 */
if (window.XMLHttpRequest) {
  const originalXHROpen = window.XMLHttpRequest.prototype.open;
  const originalXHRSend = window.XMLHttpRequest.prototype.send;
  
  // Step 1: Override the open method to capture URL information
  window.XMLHttpRequest.prototype.open = function(method, url, async, user, password) {
    // Store URL and method for later use in the send method
    this._url = url;
    this._method = method;
    return originalXHROpen.apply(this, arguments);
  };
  
  // Step 2: Override the send method to intercept specific requests
  window.XMLHttpRequest.prototype.send = function(body) {
    // Check if this is the language file request we want to block
    if (this._url && this._url.includes('assets/lang/en-US.lang')) {
      
      // Step 3: Mock a 404 error response that matches what would happen normally
      // Set all the XHR properties that would be set in a real 404 response
      Object.defineProperty(this, 'readyState', { value: 4, writable: true }); // 4 = DONE
      Object.defineProperty(this, 'status', { value: 404, writable: true });   // 404 = Not Found
      Object.defineProperty(this, 'statusText', { value: 'Not Found', writable: true });
      Object.defineProperty(this, 'responseText', { value: '404 Not Found', writable: true });
      
      // Step 4: Trigger the appropriate callback to simulate request completion
      setTimeout(() => {
        if (typeof this.onerror === 'function') {
          // Trigger error handler if available (simulating network error)
          this.onerror(new Error('404 Not Found'));
        } else if (typeof this.onload === 'function') {
          // Fall back to onload with 404 status if no error handler
          this.onload();
        }
      }, 0);
      
      // Step 5: Return early to prevent the actual request
      return;
    }
    
    // For all other requests, proceed normally
    return originalXHRSend.apply(this, arguments);
  };
}

require("jquery-ui-sortable");
require("formBuilder");

/**
 * Main FormEditor Component
 * Handles editing of existing forms with features like:
 * - Field management and validation
 * - Template form integration
 * - Column visibility control
 * - Form preview
 * - Unsaved changes detection
 */
const FormEditor = () => {
  const fb = useRef();
  const [formBuilder, setFormBuilder] = useState(null);
  const [fields, setFields] = useState([]);
  const [isBtnDisabled, setIsBtnDisabled] = useState(true);
  const [formBuilderUpdateCount, setFormBuilderUpdateCount] = useState(0);
  const [templateForms, setTemplateForms] = useState([]);
  const [listColumns, setListColumns] = useState(CHILD_FORM_DEFAULT_COLUMNS);
  const [initialFormColumnsSet, setInitialFormColumnsSet] = useState(false);
  const [isUsingDefaultColumns, setIsUsingDefaultColumns] = useState(false);
  const [error, setError] = useState("");
  const [openPreview, setOpenPreview] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [searchElements, setSearchElements] = useState("");

  const { form, setForm } = useContext(FormBuilderContext);

  const { setNotification } = useContext(LoginFormContext);
  const { id: idFromParams } = useParams();
  const navigate = useNavigate();

  // --- Form validation schema using Yup ---
  const validationSchema = Yup.object().shape({
    name: Yup.string().required(VALIDATION_MESSAGES.FORM_BUILDER.FORM_NAME_REQUIRED),
    type: Yup.object().required("Type is required"),
    prefix: Yup.string()
      .required("Prefix is required")
      .matches(/^[a-zA-Z]{2,3}$/, "Prefix must contain min. 2 to max. 3 alphabetic characters"),
    template: Yup.array(),
    list_columns: Yup.array(),
  });

  // --- Formik setup ---
  const formik = useFormik({
    initialValues: {
      name: "",
      type: { id: 1, label: "Form" },
      prefix: "",
      template: [],
      list_columns: CHILD_FORM_DEFAULT_COLUMNS,
    },
    validationSchema: validationSchema,
    enableReinitialize: true, // allow initialValues to update from API 
    onSubmit: handleSubmit,
  });

  // --- Unsaved changes detection ---
  useEffect(() => {
    if (initialFormValues) {
      setIsFormDirty(!isEqual(formik.values, initialFormValues));
    }
  }, [formik.values, initialFormValues]);
  // --- End Step 2 ---
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutationRecord) => {
      setTimeout(() => {
        setFormBuilderUpdateCount((prev) => prev + 1);
      }, 1000);
    });
  });

  /**
   * Formats a field for list columns
   * Converts form field data into column configuration format
   */
  const formatField = (field, show = true) => {
    const fieldType = {
      select: "autocomplete",
      date: "date",
      textarea: "text",
      "radio-group": "autocomplete",
      text: "text",
      checkbox: "autocomplete",
      number: "number",
    };

    const type = ["time"].includes(field?.name) ? "time" : fieldType?.[field.type];

    return {
      id: field.name,
      label: field.display_label || field.label,
      type: type,
      index: field?.sort + 5,
      show: field?.show ?? show,
      disableSort: ["court_action", "court_documents"].includes(field.name),
      disableFilter: false,
    };
  };

  /**
   * Form Builder Configuration
   * Defines behavior and appearance of the form builder
   * - Controls field positioning and buttons
   * - Handles field edit events
   * - Manages field validation
   */
  const options = useCallback(() => {
    return {
      // Set the control panel position
      controlPosition: "left",
      // Order of controls in the palette
      controlOrder: ["text", "select", "textarea"],
      // Disable certain action buttons
      disabledActionButtons: ["data", "save", "clear"],
      // Disable the copy button for all field types
      disabledFieldButtons: {
        autocomplete: ["copy"],
        button: ["copy"],
        "checkbox-group": ["copy"],
        date: ["copy"],
        file: ["copy"],
        header: ["copy"],
        hidden: ["copy"],
        number: ["copy"],
        paragraph: ["copy"],
        "radio-group": ["copy"],
        select: ["copy"],
        starRating: ["copy"],
        text: ["copy"],
        textarea: ["copy"],
        checkbox: ["copy"],
      },
      // Field edit panel logic
      async onOpenFieldEdit(editPanel) {
        // Set placeholder label
        const placeholderWrap = document.getElementsByClassName("placeholder-wrap");
        const nameWrap = document.getElementsByClassName("name-wrap");
        const formElements = document.getElementsByClassName("form-elements");
        for (let index = 0; index < placeholderWrap.length; index += 1) {
          placeholderWrap[index].children[0].innerHTML = "Display Text";
        }
        // Disable the name fields on option open
        for (let index = 0; index < nameWrap.length; index += 1) {
          // @ts-ignore
          nameWrap[index].children[1].children[0].disabled = true;
        }
        // Add min length input if not present
        for (let index = 0; index < formElements.length; index += 1) {
          const createMinLengthDiv = document.createElement("div");
          createMinLengthDiv.className = "form-group minlength-wrap";
          const createLabel = document.createElement("label");
          createLabel.innerHTML = "Min Length";
          const createInputWrapDiv = document.createElement("div");
          createInputWrapDiv.className = "input-wrap";
          const createInput = document.createElement("input");
          createInput.className = "form-control";
          createInput.name = "minlength";
          createInput.style.width = "75px";
          createInput.type = "number";
          createMinLengthDiv.appendChild(createLabel);
          createInputWrapDiv.appendChild(createInput);
          createMinLengthDiv.appendChild(createInputWrapDiv);
          let isMinLengthElementPushed = false;
          for (let j = 0; j < formElements[index].children.length; j += 1) {
            if (formElements[index].children[j].className === "form-group minlength-wrap") {
              isMinLengthElementPushed = true;
            }
          }
          if (!isMinLengthElementPushed) {
            formElements[index].appendChild(createMinLengthDiv);
          }
        }
        
        // Safely get field label with null checks
        const getFieldLabel = () => {
          try {
            // Check if the DOM structure exists before accessing
            if (editPanel?.children?.[0]?.children?.[1]?.children?.[1]?.innerText) {
              return editPanel.children[0].children[1].children[1].innerText;
            }
            return null;
          } catch (error) {
            console.warn("Could not access field label:", error);
            return null;
          }
        };
        
        const fieldLabel = getFieldLabel();
        
        // Field-specific logic for Email, Service, Date, and special fields
        if (fieldLabel === "Email") {
          const emailChildren = editPanel.children[0].children;
          for (let index = 0; index < emailChildren.length; index += 1) {
            if (
              emailChildren[index]?.className?.includes("maxlength-wrap") ||
              emailChildren[index]?.className?.includes("minlength-wrap")
            ) {
              emailChildren[index].remove();
            }
          }
        } else if (fieldLabel === "Service") {
          const serviceChildren = editPanel.children[0].children;
          for (let index = 0; index < serviceChildren.length; index += 1) {
            if (serviceChildren[index]?.className?.includes("other-wrap")) {
              serviceChildren[index].remove();
            }
          }
        } else if (fieldLabel && fieldLabel.toLowerCase().includes("date")) {
          const dateChildren = editPanel.children[0].children;
          for (let index = 0; index < dateChildren.length; index += 1) {
            if (
              dateChildren[index]?.className?.includes("maxlength-wrap") ||
              dateChildren[index]?.className?.includes("minlength-wrap") ||
              dateChildren[index]?.className?.includes("min-wrap") ||
              dateChildren[index]?.className?.includes("max-wrap") ||
              dateChildren[index]?.className?.includes("step-wrap")
            ) {
              dateChildren[index].remove();
            }
            if (dateChildren[index]?.className?.includes("max-wrap")) {
              dateChildren[index].remove();
            }
            if (dateChildren[index]?.className?.includes("step-wrap")) {
              dateChildren[index].remove();
            }
          }
        } else if (
          fieldLabel && [
            "Court Action",
            "Court Documents",
            "Adoption",
            "Appointment Appearance",
            "Appointment Timeliness",
            "Calculate Hours In Weeks Or Months",
            "Case Transfer Family Relocated",
            "Case Treatment Plan",
            "Change In Placement",
            "Compliant",
            "Did Family Services/Child Welfare Agency Remove?",
            "Engaged",
            "Face To Face",
            "Family Refused Non Compliant",
            "Family Services/Child Welfare Agency Safety Plan",
            "Family Unable To Contact Non Engaged",
            "Gender",
            "Guardianship Established",
            "Inappropriate Case",
            "Is Case Treatment Mandated?",
            "Is Case Treatment Provided By Private Payor?",
            "Kinship/Non-Kinship Foster",
            "Legal Status (Is The Court Involved?) Of Abuser",
            "Location Of Service",
            "Preparedness Before Appointment",
            "Reunification",
            "Was Non-Custodial Parent(S) And Child(Ren) Information Provided?",
          ].includes(fieldLabel)
        ) {
          // Remove Other option from the radio and checkbox type fields
          const optionChildren = editPanel.children[0].children;
          for (let index = 0; index < optionChildren.length; index += 1) {
            if (optionChildren[index]?.className?.includes("other-wrap")) {
              optionChildren[index].remove();
            }
          }
        }
        if (fieldLabel && ["Is Case Treatment Mandated?"].includes(fieldLabel)) {
          // Remove Other option from this field
          const optionChildren = editPanel.children[0].children;
          for (let index = 0; index < optionChildren.length; index += 1) {
            if (optionChildren[index]?.className?.includes("other-wrap")) {
              optionChildren[index].remove();
            }
          }
        }
      },
      // Field add logic
      onAddField(fieldId) {
        if (isBtnDisabled) {
          setIsBtnDisabled(false);
        }
        const splittedFieldId = fieldId.split("-");
        splittedFieldId[splittedFieldId.length - 1] = `${
          +splittedFieldId[splittedFieldId.length - 1] + 1
        }`;
        const finalFieldId = splittedFieldId.join("-");
        setTimeout(() => {
          const test = document.getElementById(finalFieldId);
          // Custom field logic for display label
          if (
            test?.children?.[1]?.innerText === CUSTOM_FIELD_NAME.CUSTOM_TEXT_AREA ||
            test?.children?.[1]?.innerText === CUSTOM_FIELD_NAME.CUSTOM_TEXT_FIELD
          ) {
            const textArea = test.children[5].children[0];
            const textAreaChildren = textArea.children;
            const createMinLengthDiv = document.createElement("div");
            createMinLengthDiv.className = "form-group displayLabel-wrap";
            const createLabel = document.createElement("label");
            createLabel.innerHTML = "Display Label";
            const createInputWrapDiv = document.createElement("div");
            createInputWrapDiv.className = "input-wrap";
            const createInput = document.createElement("input");
            createInput.className = "form-control";
            createInput.name = "displayLabel";
            createInput.style.width = "100%";
            createInput.type = "text";
            createInput.required = true;
            let nameInput = null;
            for (let j = 0; j < textAreaChildren.length; j += 1) {
              if (textAreaChildren[j].className === "form-group name-wrap") {
                nameInput = textAreaChildren[j].children[1].children[0];
              }
            }
            createInput.addEventListener("input", (event) => {
              const input = event.target;
              // Allow most characters for display label
              // Only restrict characters that could cause issues
              const sanitizedDisplayLabel = input.value.replace(/[^a-zA-Z0-9 ]/g, "");
              input.value = sanitizedDisplayLabel;
              // Generate valid variable name for internal use
              const validVariableName =
                sanitizedDisplayLabel
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, "_")
                  .replace(/^[^a-z]+/, "")
                  .replace(/_+$/, "") || nameInput?.value;
              if (nameInput) {
                nameInput.value = validVariableName;
              }
            });
            createMinLengthDiv.appendChild(createLabel);
            createInputWrapDiv.appendChild(createInput);
            createMinLengthDiv.appendChild(createInputWrapDiv);
            let isMinLengthElementPushed = false;
            for (let j = 0; j < textAreaChildren.length; j += 1) {
              if (textAreaChildren[j].className === "form-group displayLabel-wrap") {
                isMinLengthElementPushed = true;
              }
            }
            if (!isMinLengthElementPushed) {
              textArea.appendChild(createMinLengthDiv);
            }
          }
          // Disable duplicate/time fields
          const liTag = document.getElementsByClassName("input-control ui-sortable-handle");

          for (let index = 0; index < liTag.length; index += 1) {
            const currentFieldText = liTag[index]?.children?.[0]?.innerText || '';
            // Get only the field name from test.innerText by splitting on newlines
            const testFieldName = test?.innerText?.split('\n')?.[0]?.trim() || '';
            // Check if either the field names match exactly
            const isExactMatch = testFieldName === currentFieldText;

            // Disable field if it matches
            if (isExactMatch) {
              setError("");
              liTag[index].style.pointerEvents = "none";
              liTag[index].style.opacity = "0.6";
            }

            // Always keep custom fields enabled
            if (
              currentFieldText === CUSTOM_FIELD_NAME.CUSTOM_TEXT_AREA ||
              currentFieldText === CUSTOM_FIELD_NAME.CUSTOM_TEXT_FIELD
            ) {
              liTag[index].style.pointerEvents = "all";
              liTag[index].style.opacity = "1";
            }
          }
          // Add event listeners for remove/move
          const removeBtn = document.getElementById(`del_${finalFieldId}`);
          const moveUpBtn = document.getElementById(`${finalFieldId}-sort-higher`);
          const moveDownBtn = document.getElementById(`${finalFieldId}-sort-lower`);
          observer.observe(test, { attributes: true, attributeFilter: ["style"] });
          removeBtn.addEventListener("click", (event) => {
            for (let index = 0; index < liTag.length; index += 1) {
              const currentFieldText = liTag[index]?.children?.[0]?.innerText || '';
              const testText = test?.innerText || '';
              if (
                currentFieldText.includes(testText) ||
                testText.split("\n").includes(currentFieldText)
              ) {
                liTag[index].style.pointerEvents = "all";
                liTag[index].style.opacity = "1";
              }
            }
            setTimeout(() => {
              setFormBuilderUpdateCount((prev) => prev + 1);
            }, 300);
          });
          moveUpBtn.addEventListener("click", (event) => {
            setTimeout(() => {
              setFormBuilderUpdateCount((prev) => prev + 1);
            }, 100);
          });
          moveDownBtn.addEventListener("click", (event) => {
            setTimeout(() => {
              setFormBuilderUpdateCount((prev) => prev + 1);
            }, 100);
          });
          setTimeout(() => {
            setFormBuilderUpdateCount((prev) => prev + 1);
          }, 100);
        }, 100);
      },
      // Field edit close logic
      onCloseFieldEdit(editPanel) {
        const divOfInputField = document.getElementsByClassName("formbuilder-text form-group");
        const divOfTextAreaField = document.getElementsByClassName(
          "formbuilder-textarea form-group"
        );
        for (let index = 0; index < divOfInputField.length; index += 1) {
          divOfInputField[index].children[1].style.width = "100%";
        }
        for (let index = 0; index < divOfTextAreaField.length; index += 1) {
          divOfTextAreaField[index].children[1].style.width = "100%";
        }
        setTimeout(() => {
          setFormBuilderUpdateCount((prev) => prev + 1);
        }, 100);
      },
      // Disable most field types except text/textarea (uncomment to disable those too)
      disableFields: [
        "autocomplete",
        "button",
        "checkbox-group",
        "date",
        "file",
        "header",
        "hidden",
        "number",
        "paragraph",
        "radio-group",
        "select",
        "starRating",
        // 'text',
        // 'textarea',
      ],
      // Internationalization for custom field names
      i18n: {
        override: {
          "en-US": {
            text: CUSTOM_FIELD_NAME.CUSTOM_TEXT_FIELD,
            textArea: CUSTOM_FIELD_NAME.CUSTOM_TEXT_AREA,
          },
        },
      },
    };
  }, []);
  // --- End Advanced FormBuilder Options ---

  /**
   * API Functions
   * Handle data fetching and submission
   */

  /**
   * Fetches form elements listing
   * Gets available fields for form building
   */
  const handleFormElementListing = async () => {
    try {
      const response = await getFormElements();
      if (response?.status === 200) {
        setFields(response.data.data);
      } else {
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: response?.data?.message || "Failed to fetch form elements",
          open: true,
        });
      }
    } catch (error) {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: error?.response?.data?.message || "Failed to fetch form elements",
        open: true,
      });
    }
  };

  /**
   * Fetches all template forms
   * Gets available templates for form association
   */
  const handleGetAllTemplateForms = async () => {
    try {
      const response = await getAllTemplateFormsApi();
      if (response?.status === 200) {
        const data = response.data.data.rows;
        const templateData =
          data
            ?.map((template) => ({
              ...template,
              id: template["id"],
              label: template["name"],
            }))
            ?.sort((a, b) => a.label.localeCompare(b.label)) || [];
        setTemplateForms(templateData);
      } else {
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: response?.data?.message || "Failed to fetch template forms",
          open: true,
        });
      }
    } catch (error) {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: error?.response?.data?.message || "Failed to fetch template forms",
        open: true,
      });
    }
  };

  /**
   * Fetches single form data
   * Gets existing form data for editing
   * - Handles form type conversion
   * - Processes template associations
   * - Sets up column configuration
   */
  const getSingleForm = useCallback(async () => {
    try {
      const response = await getSingleFormData(idFromParams);
      if (response.status === 200) {
        // Handle form_type as number (1 or 2) and convert to object
        const formType = response.data.data.form_type;
        const typeObject = formType === 1 ? { id: 1, label: "Form" } : { id: 2, label: "Template" };

        // Handle templates_used - extract template IDs from the nested structure
        const apiTemplatesUsed = response.data.data.templates_used || [];
        const processedTemplates = apiTemplatesUsed.map((templateUsed) => {
          // Extract template object from the nested structure
          const template = templateUsed.template;
          return {
            ...template,
            id: template.id,
            label: template.name,
          };
        });

        // Initialize list columns with either existing columns or defaults
        const existingColumns = response.data.data.list_columns?.list_columns;
        const initialColumns = existingColumns || CHILD_FORM_DEFAULT_COLUMNS;
        setListColumns(initialColumns);
        setInitialFormColumnsSet(true);
        setIsUsingDefaultColumns(!existingColumns);

        const formValues = {
          name: response.data.data.name,
          type: typeObject,
          prefix: response.data.data.prefix,
          template: processedTemplates,
          list_columns: initialColumns,
        };
        formik.setValues(formValues);
        setInitialFormValues(formValues);

        handleForm(response.data.data.service_add_form_fields);
      } else {
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: response.data.message,
          open: true,
        });
      }
    } catch (error) {
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: error.message || "Something went wrong!",
        open: true,
      });
    }
  }, [idFromParams, setNotification]);

  /**
   * Form Data Management Functions
   */

  /**
   * Gets parsed form data
   * Extracts and normalizes form field data
   * - Processes minLength values
   * - Handles custom field display labels
   * - Sets field IDs and sort order
   */
  function getParsedFormData() {
    // Get all minLength and displayLabel DOM elements
    const minLength = document.getElementsByName("minlength");
    const displayLabel = document.getElementsByName("displayLabel");
    // Parse the formBuilder data
    const parsedDataInstance = formBuilder?.formData ? JSON.parse(formBuilder.formData) : [];

    // Set minlength for each field if > 0
    for (let index = 0; index < minLength.length; index += 1) {
      for (let j = 0; j < parsedDataInstance.length; j += 1) {
        if (index === j && parsedDataInstance[j].type !== "select") {
          const minVal = +minLength[index].value;
          parsedDataInstance[j] = {
            ...parsedDataInstance[j],
            minlength: minVal > 0 ? minVal : undefined,
          };
        }
      }
    }

    // Set display_label for custom fields (CUSTOM_TEXT_AREA, CUSTOM_TEXT_FIELD)
    let processedCount = 0;
    for (let index = 0; index < displayLabel.length; index += 1) {
      const j = parsedDataInstance.findIndex((item, idx) => {
        const isMatch =
          (item?.label === CUSTOM_FIELD_NAME.CUSTOM_TEXT_AREA && item?.type === "textarea") ||
          (item?.label === CUSTOM_FIELD_NAME.CUSTOM_TEXT_FIELD && item?.type === "text");
        const isAfterProcessed = idx >= processedCount;
        return isMatch && isAfterProcessed;
      });
      if (j !== -1) {
        parsedDataInstance[j] = {
          ...parsedDataInstance[j],
          display_label: displayLabel[index].value?.trim() || "",
          is_custom: true,
        };
        processedCount = j + 1;
      }
    }

    // Set field_id and sort index for each field
    parsedDataInstance.forEach((element, index) => {
      const matchingField = fields?.find((field) => element.name === field.name);
      element["sort"] = index + 1;
      element["field_id"] = matchingField ? +matchingField.id : null;
    });

    return parsedDataInstance;
  }

  /**
   * Validates parsed form data
   * Checks for required fields and duplicates
   */
  function validateParsedFormData(parsedDataInstance) {
    // Check for missing display_label in custom fields
    const hadDisplayLabel = parsedDataInstance.some(
      (field) =>
        !field.display_label?.trim() &&
        [CUSTOM_FIELD_NAME.CUSTOM_TEXT_AREA, CUSTOM_FIELD_NAME.CUSTOM_TEXT_FIELD].includes(
          field.label
        )
    );
    if (hadDisplayLabel) {
      return "Display labels cannot be empty. Please provide a display label for all fields.";
    }
    // Check for empty field names
    const fieldNames = parsedDataInstance.map((field) => field.name);
    const hasEmptyNames = fieldNames.some((name) => !name?.trim());
    if (hasEmptyNames) {
      return "Field names cannot be empty. Please provide a name for all fields.";
    }
    // Check for duplicate field names
    const hasDuplicates = fieldNames.some((name, index) => fieldNames.indexOf(name) !== index);
    if (hasDuplicates) {
      return "Duplicate field names found. Each field must have a unique name.";
    }
    return null;
  }

  /**
   * Saves form data
   * Validates and processes form before saving
   */
  async function saveData() {
    const parsedDataInstance = getParsedFormData();
    const errorMsg = validateParsedFormData(parsedDataInstance);
    if (errorMsg) {
      setError(errorMsg);
      return;
    }
    setError("");
    setForm(parsedDataInstance);
  }

  /**
   * Clears form data
   * Resets form builder to initial state
   */
  function clearData() {
    formBuilder.actions.clearFields();
    setForm({});

    const liTag = document.getElementsByClassName("input-control ui-sortable-handle");
    for (let index = 0; index < liTag.length; index++) {
      liTag[index].style.pointerEvents = "all";
      liTag[index].style.opacity = "1";
    }
  }

  /**
   * Form Event Handlers
   */

  /**
   * Handles form population
   * Populates the form builder with fields based on parsed form data
   */
  const handleForm = (form) => {
    const formElements = [];

    // Simple retry mechanism for slow network
    const populateForm = (retryCount = 0) => {
      const textType = document.getElementsByClassName(
        "formbuilder-icon-text input-control ui-sortable-handle"
      );
      const numberType = document.getElementsByClassName(
        "formbuilder-icon-number input-control ui-sortable-handle"
      );
      const selectType = document.getElementsByClassName(
        "formbuilder-icon-select input-control ui-sortable-handle"
      );
      const textAreaType = document.getElementsByClassName(
        "formbuilder-icon-textarea input-control  ui-sortable-handle"
      );
      const radioGroupType = document.getElementsByClassName(
        "formbuilder-icon-radio-group input-control ui-sortable-handle"
      );
      const checkboxType = document.getElementsByClassName(
        "formbuilder-icon-checkbox input-control ui-sortable-handle"
      );
      const dateType = document.getElementsByClassName(
        "formbuilder-icon-date input-control ui-sortable-handle"
      );

      // Check if form builder is ready
      if (textType.length === 0 && numberType.length === 0 && selectType.length === 0 && 
          textAreaType.length === 0 && radioGroupType.length === 0 && checkboxType.length === 0 && 
          dateType.length === 0 && retryCount < 5) {
        setTimeout(() => populateForm(retryCount + 1), 200);
        return;
      }

      for (let index = 0; index < textType.length; index++) {
        const obj = {
          label: textType[index]?.children?.[0]?.innerText || '',
          className: textType[index].className,
        };
        formElements.push(obj);
      }

      for (let index = 0; index < numberType.length; index++) {
        const obj = {
          label: numberType[index]?.children?.[0]?.innerText || '',
          className: numberType[index].className,
        };
        formElements.push(obj);
      }

      for (let index = 0; index < selectType.length; index++) {
        const obj = {
          label: selectType[index]?.children?.[0]?.innerText || '',
          className: selectType[index].className,
        };
        formElements.push(obj);
      }

      for (let index = 0; index < textAreaType.length; index++) {
        const obj = {
          label: textAreaType[index]?.children?.[0]?.innerText || '',
          className: textAreaType[index].className,
        };
        formElements.push(obj);
      }

      for (let index = 0; index < radioGroupType.length; index++) {
        const obj = {
          label: radioGroupType[index]?.children?.[0]?.innerText || '',
          className: radioGroupType[index].className,
        };
        formElements.push(obj);
      }

      for (let index = 0; index < checkboxType.length; index++) {
        const obj = {
          label: checkboxType[index]?.children?.[0]?.innerText || '',
          className: checkboxType[index].className,
        };
        formElements.push(obj);
      }

      for (let index = 0; index < dateType.length; index++) {
        const obj = {
          label: dateType[index]?.children?.[0]?.innerText || '',
          className: dateType[index].className,
        };
        formElements.push(obj);
      }

      for (let i = 0; i < form.length; i++) {
        for (let j = 0; j < formElements.length; j++) {
          if (form[i].label == formElements[j].label) {
            const domElement = document.getElementsByClassName(formElements[j].className);
            if (domElement[0]) {
              domElement[0].click();
            }
          } else if (form[i].label.includes("&amp;")) {
            // due to html to string conversion, &  converts as &amp; so we need to replace it
            const replacedText = form[i].label.replace("&amp;", "&");
            if (replacedText == formElements[j].label) {
              const domElement = document.getElementsByClassName(formElements[j].className);
              if (domElement[0]) {
                domElement[0].click();
              }
            }
          }
        }
      }
    };

    populateForm();

    // Additional form setup after field population
    setTimeout(() => {
      const placeholderWrap = document.getElementsByClassName("placeholder-wrap");
      const nameWrap = document.getElementsByClassName("name-wrap");

      const fElements = document.getElementsByClassName("form-elements");

      for (let index = 0; index < placeholderWrap.length; index += 1) {
        placeholderWrap[index].children[0].innerHTML = "Display Text";
      }

      // Disable the name fields on option open
      for (let index = 0; index < nameWrap.length; index += 1) {
        // @ts-ignore
        nameWrap[index].children[1].children[0].disabled = true;
      }

      for (let index = 0; index < fElements.length; index += 1) {
        const createMinLengthDiv = document.createElement("div");
        createMinLengthDiv.className = "form-group minlength-wrap";

        const createLabel = document.createElement("label");
        createLabel.innerHTML = "Min Length";

        const createInputWrapDiv = document.createElement("div");
        createInputWrapDiv.className = "input-wrap";

        const createInput = document.createElement("input");
        createInput.className = "form-control";
        createInput.name = "minlength";
        createInput.style.width = "75px";
        createInput.type = "number";

        createMinLengthDiv.appendChild(createLabel);
        createInputWrapDiv.appendChild(createInput);
        createMinLengthDiv.appendChild(createInputWrapDiv);

        let isMinLengthElementPushed = false;

        for (let j = 0; j < fElements[index].children.length; j += 1) {
          if (fElements[index].children[j].className === "form-group minlength-wrap") {
            isMinLengthElementPushed = true;
          }
        }
        if (!isMinLengthElementPushed) {
          fElements[index].appendChild(createMinLengthDiv);
        }

        if (
          fElements[index]?.children?.[1]?.children?.[1]?.innerText ===
            CUSTOM_FIELD_NAME.CUSTOM_TEXT_AREA ||
          fElements[index]?.children?.[1]?.children?.[1]?.innerText === CUSTOM_FIELD_NAME.CUSTOM_TEXT_FIELD
        ) {
          const textArea = fElements[index];
          const textAreaChildren = textArea.children;

          const createMinLengthDiv = document.createElement("div");
          createMinLengthDiv.className = "form-group displayLabel-wrap";

          const createLabel = document.createElement("label");
          createLabel.innerHTML = "Display Label";

          const createInputWrapDiv = document.createElement("div");
          createInputWrapDiv.className = "input-wrap";

          const createInput = document.createElement("input");
          createInput.className = "form-control";
          createInput.name = "displayLabel";
          createInput.style.width = "100%";
          createInput.type = "text";
          createInput.required = true;

          let nameInput = null;

          for (let j = 0; j < textAreaChildren.length; j += 1) {
            if (textAreaChildren[j].className === "form-group name-wrap") {
              nameInput = textAreaChildren[j].children[1].children[0];
            }
          }

          createInput.addEventListener("input", (event) => {
            const input = event.target;

            // Allow most characters for display label
            // Only restrict characters that could cause issues
            const sanitizedDisplayLabel = input.value.replace(/[^a-zA-Z0-9 ]/g, "");
            input.value = sanitizedDisplayLabel;

            // Generate valid variable name for internal use
            // const validVariableName =
            //   sanitizedDisplayLabel
            //     // Convert to lowercase
            //     .toLowerCase()
            //     // Replace spaces and special chars with underscore
            //     .replace(/[^a-z0-9]+/g, '_')
            //     // Remove leading numbers/special chars
            //     .replace(/^[^a-z]+/, '')
            //     // Remove trailing special chars
            //     .replace(/_+$/, '') ||
            //   // Handle empty string
            //   nameInput?.value;

            // if (nameInput) {
            //   nameInput.value = validVariableName;
            // }
          });

          createMinLengthDiv.appendChild(createLabel);
          createInputWrapDiv.appendChild(createInput);
          createMinLengthDiv.appendChild(createInputWrapDiv);

          let isMinLengthElementPushed = false;

          for (let j = 0; j < textAreaChildren.length; j += 1) {
            if (textAreaChildren[j].className === "form-group displayLabel-wrap") {
              isMinLengthElementPushed = true;
            }
          }

          if (!isMinLengthElementPushed) {
            textArea.appendChild(createMinLengthDiv);
          }
        }
      }

      /**
       * When the edit form is opened, the form elements are added to the form builder
       * When the elements are added, by default the forms scrolls itself to the bottom
       * This is to prevent the form from scrolling to the bottom and also prevent the ul list to scroll itself to the bottom.
       */
      // Safely handle scrolling
      try {
        window.scrollTo(0, 0);
        const ulElement = document.querySelector(".frmb.stage-wrap.ui-sortable");
        // Only set scrollTop if element exists
        if (ulElement) {
          ulElement.scrollTop = 0;
        }
      } catch (error) {}

      const handleTextAreaFieldsUpdate = (mainTextAreaDiv, form) => {
        // Create a map to track how many times we've seen each label
        const labelInstanceCount = {};

        Array.from(mainTextAreaDiv).forEach((textAreaDiv) => {
          const firstChildLot = textAreaDiv.children;
          const fieldLabel = firstChildLot[1];

          if (fieldLabel?.className !== "field-label") return;

          const labelText = fieldLabel?.innerText || '';

          // Initialize or increment the instance counter for this label
          labelInstanceCount[labelText] = (labelInstanceCount[labelText] || 0) + 1;
          const currentInstance = labelInstanceCount[labelText];

          // Find all form elements with this label
          const matchingFormElements = form.filter(
            (formElement) => formElement.label === labelText
          );

          // Get the corresponding form element for this instance
          const matchingFormElement = matchingFormElements[currentInstance - 1];
          if (!matchingFormElement) return;

          const editFormElements = firstChildLot[5]?.children;
          if (!editFormElements) return;

          Array.from(editFormElements).forEach((editElement) => {
            const individualChildren = editElement.children;

            Array.from(individualChildren).forEach((child) => {
              if (child?.innerText?.toLowerCase() === "display label") {
                const input = child.children[1]?.children[0];
                if (input) {
                  input.value = String(
                    matchingFormElement.display_label || matchingFormElement.name
                  );
                }
              }
              // Handle min length field
              if (child?.innerText?.toLowerCase() === "min length" && matchingFormElement.minlength) {
                const input = child.children[1]?.children[0];
                if (input) {
                  input.value = String(matchingFormElement.minlength);
                }
              }

              // Handle required field
              if (child?.innerText?.toLowerCase() === "required") {
                if (matchingFormElement.required) {
                  firstChildLot[2].style.display = "inline";
                }
                const checkbox = child.children[1]?.children[0];
                if (checkbox) {
                  checkbox.checked = matchingFormElement.required;
                }
              }

              // Handle multiple checkbox for select/radio fields
              if (child.children[1]?.children[0]?.name === "multiple") {
                const checkbox = child.children[1]?.children[0];
                if (checkbox) {
                  checkbox.checked = matchingFormElement.multiple || false;
                }
              }

              // Handle other checkbox for radio fields
              if (child.children[1]?.children[0]?.name === "other") {
                const checkbox = child.children[1]?.children[0];
                if (checkbox) {
                  checkbox.checked = matchingFormElement.other || false;
                }
              }

              // Handle other form fields
              const input = child.children[1]?.children[0];
              if (input?.name && matchingFormElement[input.name]) {
                input.value = matchingFormElement[input.name];

                // Special handling for description field
                if (input.name === "description") {
                  const tooltipElement = firstChildLot[3];
                  if (tooltipElement) {
                    tooltipElement.style.display = "inline-block";
                    tooltipElement.setAttribute("tooltip", matchingFormElement[input.name]);
                  }
                }
              }
            });
          });
        });
      };

      // Helper function to get the class name for each field type
      const getFieldClassName = (fieldType) => {
        const classNameMap = {
          textArea: "textarea-field form-field",
          text: "text-field form-field",
          select: "select-field form-field",
          radio: "radio-group-field form-field",
          checkbox: "checkbox-field form-field",
          date: "date-field form-field",
        };
        return classNameMap[fieldType] || "";
      };

      // Update all field types
      const fieldTypes = ["textArea", "text", "select", "radio", "checkbox", "date"];
      fieldTypes.forEach((fieldType) => {
        const className = getFieldClassName(fieldType);
        if (className) {
          handleTextAreaFieldsUpdate(document.getElementsByClassName(className), form);
        }
      });
    }, 550);

    setIsBtnDisabled(false);
  };
  // Watch form values for conditional rendering
  const watchType = formik.values.type;

  /**
   * Enhanced handleSubmit: Validate fields before submit
   * Validates and processes form data before API submission
   */
  async function handleSubmit(values) {
    try {
      formik.setSubmitting(true);
      // Always get the latest parsed form data
      const parsedDataInstance = getParsedFormData();
      const errorMsg = validateParsedFormData(parsedDataInstance);
      if (errorMsg) {
        setError(errorMsg);
        formik.setSubmitting(false);
        return;
      }
      if (!parsedDataInstance.length) {
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: "Please select some fields to create a form",
          open: true,
        });
        formik.setSubmitting(false);
        return;
      }
      // --- Dynamic list_columns update: Add/remove columns based on fields ---
      if (values.type.id !== 2) {
        // Only for non-template forms
        const fDataIds = new Set([
          ...(parsedDataInstance?.map((f) => f.name) || []),
          ...(CHILD_FORM_DEFAULT_COLUMNS?.map((f) => f.id) || []),
        ]);
        const prevIds = new Set((values.list_columns || []).map((f) => f.id));
        // Add new fields from parsedDataInstance not in prev
        const toAdd = parsedDataInstance
          .filter((f) => !prevIds.has(f.name))
          .map((field) => ({
            id: field.name,
            label: field.display_label || field.label,
            type: field.type,
            index: field.sort + 5,
            show: true,
            disableSort: ["court_action", "court_documents"].includes(field.name),
            disableFilter: false,
          }));
        // Keep only fields in prev that are still in fData (removes orphaned fields)
        const toKeep = (values.list_columns || []).filter((f) => fDataIds.has(f.id));
        // Always ensure action column is last
        const columnsWithoutAction = [...toKeep, ...toAdd].filter((col) => col.id !== "action");
        const actionColumn = CHILD_FORM_DEFAULT_COLUMNS.find((col) => col.id === "action");
        let updatedColumns = columnsWithoutAction;
        if (actionColumn) {
          const maxIndex = Math.max(...columnsWithoutAction.map((col) => col.index || 0), 0);
          const updatedActionColumn = {
            ...actionColumn,
            index: maxIndex + 1,
          };
          updatedColumns = [...columnsWithoutAction, updatedActionColumn];
        }
        formik.setFieldValue("list_columns", updatedColumns);
      }
      // Prepare form data with new structure
      const formBuilderData = {
        name: values.name,
        form_type: values.type.id, // Send form_type as number (1 or 2)
        prefix: values.prefix,
        template: values.template.map((template) => +template.id), // Only pass template IDs
        form: parsedDataInstance,
        visible_fields:
          values.type.id === 2 ? undefined : values.list_columns || CHILD_FORM_DEFAULT_COLUMNS,
      };
      const response = await updateForm({ ...formBuilderData, form_id: idFromParams });
      if (response && response.status === 200) {
        formik.setSubmitting(false);
        setNotification({
          color: "success",
          icon: "check",
          title: "Success",
          content: response.data.message,
          open: true,
        });
        navigate("/form-builder");
      } else {
        formik.setSubmitting(false);
        setNotification({
          color: "error",
          icon: "cancel",
          title: "Error",
          content: response.data.message,
          open: true,
        });
      }
    } catch (error) {
      formik.setSubmitting(false);
      setNotification({
        color: "error",
        icon: "cancel",
        title: "Error",
        content: error.message || "Something went wrong!",
        open: true,
      });
    }
  }

  /**
   * Column Management
   * Handles visibility toggling of form columns
   */
  const handleColumnChange = (id, show) => {
    const updatedColumns = listColumns.map((item) => {
      if (item.id === id) {
        return { ...item, show };
      }
      return item;
    });
    setListColumns(updatedColumns);
    formik.setFieldValue("list_columns", updatedColumns);
  };

  /**
   * Preview Dialog Handlers
   */
  const handlePreviewOpen = () => {
    setOpenPreview(true);
  };

  const handlePreviewClose = () => {
    setOpenPreview(false);
  };

  /**
   * Unsaved Changes Warning
   * Prevents accidental navigation away from unsaved changes
   */
  const handleBeforeUnload = (e) => {
    if (isFormDirty) {
      e.preventDefault();
      e.returnValue = "";
    }
  };

  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isFormDirty]);

  // --- Enhanced unsaved changes detection: Deep compare form fields ---
  useEffect(() => {
    if (initialFormValues) {
      // Deep compare both formik.values and parsed form fields
      const parsedDataInstance = getParsedFormData();
      const isFieldsDirty = !isEqual(parsedDataInstance, form);
      const isValuesDirty = !isEqual(formik.values, initialFormValues);
      setIsFormDirty(isFieldsDirty || isValuesDirty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values, initialFormValues, formBuilder?.formData, form]);

  /**
   * Initialize form elements and templates
   * Fetches initial data when component mounts
   */
  useEffect(() => {
    handleFormElementListing();
    handleGetAllTemplateForms();
  }, []);

  /**
   * Initialize form builder
   * Sets up jQuery form builder with configured options
   */
  useEffect(() => {
    if (fb.current) {
      $(fb.current)
        .formBuilder({
          ...options(),
          fields,
        })
        .promise.then((formBuilder) => {
          setFormBuilder(formBuilder);

          // Sort form elements alphabetically
          const items = $(".frmb-control.ui-sortable > li").get();
          items.sort(function (a, b) {
            const keyA = $(a).text();
            const keyB = $(b).text();

            if (keyA < keyB) return -1;
            if (keyA > keyB) return 1;
            return 0;
          });
          const ul = $(".frmb-control.ui-sortable");
          $.each(items, function (i, li) {
            ul.append(li);
          });

          // Add tooltip for every li tag
          items.forEach((item) => {
            if (item?.innerText) {
              item.title = item.innerText;
            }
          });
        });

      setTimeout(() => {
        const dragArea = document.getElementsByClassName("frmb stage-wrap ui-sortable empty");
        if (dragArea.item(0)) {
          dragArea.item(0).dataset.content = DRAG_AREA_TEXT;
        }
      }, 100);
    }
  }, [fields, options]);


  /**
   * Load single form data
   * Fetches and populates form data when ID changes
   */
  useEffect(() => {
    if (idFromParams) {
      getSingleForm();
    }
  }, [idFromParams]);

  /**
   * Search filter effect
   * Filters form builder palette based on search input
   */
  useEffect(() => {
    const items = window.$(".frmb-control.ui-sortable > li").get();
    const ul = window.$(".frmb-control.ui-sortable");
    let hasMatches = false;

    for (let i = 0; i < items.length; i++) {
      const txtValue = items[i]?.textContent || items[i]?.innerText || '';
      if (txtValue.toUpperCase().includes(searchElements?.toUpperCase())) {
        items[i].style.display = "";
        hasMatches = true;
      } else {
        items[i].style.display = "none";
      }
    }

    // Remove previous 'No Matches Found' if present
    const noMatchesItem = document.querySelector(".no-matches");
    if (noMatchesItem) {
      window.$(".no-matches").remove();
    }

    // If no matches, add the 'No Matches Found' item
    if (!hasMatches) {
      const noMatchesLi = document.createElement("li");
      noMatchesLi.textContent = "No Matches Found";
      noMatchesLi.className = "no-matches";
      ul.append(noMatchesLi);
    }
  }, [searchElements]);

  /**
   * Column sync effect
   * Automatically syncs list columns with form fields
   * - Updates when fields change
   * - Maintains column order
   * - Preserves mandatory columns
   */
  useEffect(() => {
    if (!formBuilder || watchType?.id === 2) return;

    const fData = getParsedFormData();
    if (!fData?.length) return;

    const lColumn = listColumns || [];
    const fDataIds = new Set([
      ...(fData?.map((f) => f.name) || []),
      ...(CHILD_FORM_DEFAULT_COLUMNS?.map((f) => f.id) || []),
    ]);
    const prevIds = new Set(lColumn.map((f) => f.id));

    // Add new fields from fData that are not in prev
    const toAdd = fData.filter((f) => !prevIds.has(f.name))?.map((field) => formatField(field));

    // Keep only fields in prev that are still in fData
    const toKeep = lColumn.filter((f) => fDataIds.has(f.id));

    const hasChanges = toAdd?.length > 0 || toKeep.length !== lColumn.length;
    if (!hasChanges) return;

    // Always ensure action column is last
    const columnsWithoutAction = [...toKeep, ...toAdd].filter((col) => col.id !== "action");
    const actionColumn = CHILD_FORM_DEFAULT_COLUMNS.find((col) => col.id === "action");

    let updatedColumns = columnsWithoutAction;
    if (actionColumn) {
      const maxIndex = Math.max(...columnsWithoutAction.map((col) => col.index || 0), 0);
      const updatedActionColumn = {
        ...actionColumn,
        index: maxIndex + 1,
      };
      updatedColumns = [...columnsWithoutAction, updatedActionColumn];
    }

    setListColumns(updatedColumns);
    formik.setFieldValue("list_columns", updatedColumns);
  }, [formBuilderUpdateCount, formBuilder, fields, listColumns, watchType]);

  /**
   * UI Structure
   */
  return (
    <>
      {fields?.length > 0 ? (
        <>
          <form onSubmit={formik.handleSubmit} noValidate>
            {/* Form Header Section */}
            <MDBox
              sx={{
                mb: 3,
                display: "flex",
                justifyContent: "space-between",
                flexDirection: "row",
                gap: 1,
              }}
            >
              {/* Form Name Input */}
              <TextField
                type="text"
                value={formik.values.name}
                error={Boolean(formik.touched.name && formik.errors.name)}
                fullWidth
                label="Form Name"
                name="name"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                required
              />

              {/* Form Type Selection */}
              <MDAutocomplete
                value={formik.values.type}
                sx={{
                  p: 0,
                  maxHeight: "40px",
                  "& .MuiInputBase-root": {
                    p: "4px",
                  },
                }}
                fullWidth
                onChange={(event, newValue) => {
                  formik.setFieldValue("type", newValue);
                }}
                disabled
                options={[
                  { id: 1, label: "Form" },
                  { id: 2, label: "Template" },
                ]}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Type"
                    sx={{ p: 0 }}
                    error={Boolean(formik.touched.type && formik.errors.type)}
                    helperText={formik.touched.type && formik.errors.type}
                    required
                  />
                )}
              />

              {/* Form Prefix Input */}
              <TextField
                type="text"
                value={formik.values.prefix}
                error={Boolean(formik.touched.prefix && formik.errors.prefix)}
                helperText={formik.touched.prefix && formik.errors.prefix}
                fullWidth
                label="Prefix"
                name="prefix"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                required
              />
            </MDBox>

            {/* Search and Preview Section */}
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
              {/* Search Field */}
              <TextField
                type="text"
                value={searchElements}
                onChange={(e) => setSearchElements(e.target.value)}
                fullWidth
                label="Search Fields"
                name="search_elements"
                sx={{ maxWidth: 300 }}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setSearchElements("")}>
                      {" "}
                      <span className="material-icons">clear</span>{" "}
                    </IconButton>
                  ),
                }}
              />
              {/* Preview Button */}
              <Button
                variant="contained"
                color="secondary"
                size="small"
                onClick={handlePreviewOpen}
              >
                Preview
              </Button>
            </Box>

            {/* Form Builder Area */}
            <Box ref={fb} className="form-builder-area" id="form-builder-area" />

            {/* Template Forms Section */}
            {watchType?.id === 1 && (
              <Grid>
                <MDTypography variant="h5" sx={{ mt: 5, mb: 2 }}>
                  Template Forms
                </MDTypography>
                {templateForms.length === 0 ? (
                  <MDTypography variant="caption" color="warning.main" sx={{ mt: 1 }}>
                    No templates available
                  </MDTypography>
                ) : (
                  <Grid container spacing={2}>
                    {templateForms.map((template) => (
                      <Grid item xs={12} sm={4} key={template.id}>
                        <Checkbox
                          checked={formik.values.template.some((t) => t.id === template.id)}
                          onChange={(event) => {
                            const isChecked = event.target.checked;
                            const currentTemplates = formik.values.template;
                            if (isChecked) {
                              formik.setFieldValue("template", [...currentTemplates, template]);
                            } else {
                              formik.setFieldValue(
                                "template",
                                currentTemplates.filter((t) => t.id !== template.id)
                              );
                            }
                          }}
                          inputProps={{ "aria-label": template.label }}
                        />
                        <MDTypography
                          component="span"
                          sx={{ ml: 1, verticalAlign: "middle", fontSize: "16px" }}
                        >
                          {template.label}
                        </MDTypography>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Grid>
            )}

            {/* Column Visibility Section */}
            {watchType?.id !== 2 && (
              <Grid sx={{ my: 2 }}>
                {/* Section Title */}
                <MDTypography variant="h5" sx={{ mb: 2 }}>
                  Visible Columns
                </MDTypography>
                {/* Section Description */}
                <MDTypography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Toggle which columns are visible in the case form table. Mandatory columns cannot
                  be hidden.
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                {/* Columns as checkboxes with tooltip for mandatory columns */}
                <Grid container spacing={2}>
                  {listColumns?.map((item) => {
                    // Check if the column is mandatory
                    const isMandatory = CHILD_FORM_MANDATORY_COLUMNS.includes(item.id);
                    return (
                      <Grid item xs={12} sm={4} md={4} key={item.id}>
                        {/* Tooltip for mandatory columns */}
                        <Tooltip
                          title={isMandatory ? "This column is mandatory and cannot be hidden" : ""}
                          arrow
                          placement="top"
                        >
                          <span>
                            {/* Checkbox for column visibility */}
                            <Checkbox
                              checked={item.show}
                              onChange={() =>
                                !isMandatory && handleColumnChange(item.id, !item.show)
                              }
                              disabled={isMandatory}
                              inputProps={{ "aria-label": item.label }}
                              sx={{
                                "&.Mui-disabled": {
                                  opacity: 0.5,
                                  cursor: "not-allowed",
                                },
                              }}
                            />
                            {/* Column label */}
                            <MDTypography
                              component="span"
                              sx={{ ml: 1, verticalAlign: "middle", fontSize: "16px" }}
                            >
                              {item.label}
                            </MDTypography>
                          </span>
                        </Tooltip>
                      </Grid>
                    );
                  })}
                </Grid>
                {/* Warning if only mandatory columns are visible */}
                {listColumns?.filter((c) => c.show && !CHILD_FORM_MANDATORY_COLUMNS.includes(c.id))
                  .length === 0 && (
                  <MDTypography variant="caption" color="warning.main" sx={{ mt: 1 }}>
                    Only mandatory columns are currently visible.
                  </MDTypography>
                )}
              </Grid>
            )}

            {/* Error Display */}
            {!!error && (
              <FormHelperText error={!!error}>
                {error ?? "Please add at least one field to the form"}
              </FormHelperText>
            )}

            {/* Action Buttons */}
            <MDBox sx={{ display: "flex", justifyContent: "center", flexDirection: "row", gap: 1 }}>
              {/* Update Button */}
              <Button
                variant="contained"
                color="primary"
                type="submit"
                onClick={saveData}
                disabled={formik.isSubmitting}
              >
                Update
              </Button>

              {/* Cancel Button */}
              <MDLink to="/form-builder">
                <MDButton variant="outlined" color="primary">
                  Cancel
                </MDButton>
              </MDLink>
            </MDBox>
          </form>
        </>
      ) : (
        // Loading State
        <Box
          className="main-container"
          style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
        >
          <CircularProgress color="success" />
        </Box>
      )}

      {/* Form Preview Dialog */}
      <Dialog open={openPreview} onClose={handlePreviewClose} maxWidth="xl" fullWidth>
        <DialogTitle>Form Preview</DialogTitle>
        <DialogContent>
          <ViewFormPreview
            defaultFieldData={formBuilder?.formData ? JSON.parse(formBuilder?.formData) : []}
            watchTemplate={watchType?.id === 1 ? formik.values.template : []}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePreviewClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FormEditor;
